import React, { useState, useRef, useEffect } from "react";
import "@/App.css";
import emailjs from '@emailjs/browser';
import { 
  Phone, MapPin, Car, Plane, Heart, Users, Train,
  Tv, Wifi, Battery, Armchair, Send,
  Clock, Shield, Menu, X, Play, Gamepad2, Youtube, Music,
  Sparkles, MapPinned, CreditCard, RefreshCcw, CheckCircle, Globe, Facebook, Mail
} from "lucide-react";
import { Toaster, toast } from "sonner";
import Autocomplete from "react-google-autocomplete";

// Imports pour la carte Leaflet
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Polyline, CircleMarker, useMap } from 'react-leaflet';

// Assets
const HERO_VIDEO = "https://customer-assets.emergentagent.com/job_077dd621-b5f9-4073-b720-cb74b87f2162/artifacts/0baudyxd_SaveClip.App_AQPs-JErbLRdrLPS6nUqzlf_cgTzJgV2bm85Z-X22VnPpt1Jz41v5AW1aTEPHKs62sLtoVIgFCkqMjSqCFSfH4k07WPQh11qmZziM08.mp4";
const CAR_IMAGE = "https://customer-assets.emergentagent.com/job_077dd621-b5f9-4073-b720-cb74b87f2162/artifacts/9bierroo_SaveClip.App_623043165_17845794822674355_8578601768934681980_n.jpg";

const SERVICE_IMAGES = {
  mariage: "https://images.pexels.com/photos/5966184/pexels-photo-5966184.jpeg?auto=compress&cs=tinysrgb&w=800",
  aeroport: "/images/aeoroport.jpeg",
  gare: "https://images.pexels.com/photos/3921535/pexels-photo-3921535.jpeg?auto=compress&cs=tinysrgb&w=800",
  evenement: "/images/derriere.jpeg",
  tourisme: "https://images.pexels.com/photos/13282617/pexels-photo-13282617.jpeg?auto=compress&cs=tinysrgb&w=800",
  disposition: "/images/tableauBord.jpeg"
};

const CAR_INTERIOR_IMAGE = "/images/interieur.png";

// --- CONTACTS ---
const PHONE_NUMBER = "07 61 52 29 40";
const PHONE_LINK = "tel:+33761522940";
const EMAIL_ADDRESS = "contact@shtransport.fr";
const EMAIL_LINK = `mailto:${EMAIL_ADDRESS}`;
const FACEBOOK_LINK = "https://www.facebook.com/profile.php?id=61584879792185";
const WHATSAPP_LINK = "https://wa.me/33761522940?text=Bonjour%20SH%20Transport,%20je%20souhaite%20réserver%20une%20course.";

// --- TRADUCTIONS ---
const translations = {
  FR: {
    nav_sim: "Simulateur", nav_serv: "Services", nav_veh: "Véhicule", nav_cont: "Contact",
    hero_tag: "VTC Premium en Normandie", hero_title1: "Votre Chauffeur Privé", hero_title2: "en Normandie", 
    hero_desc: "Voyagez en Première Classe à bord de notre Mercedes Classe E Hybride. Confort absolu, discrétion et respect de l'environnement pour tous vos transferts.",
    btn_estimate: "Estimer mon trajet", btn_call: "Appeler maintenant",
    sim_tag: "Simulateur en ligne", sim_title1: "Estimez votre ", sim_title2: "trajet", 
    sim_desc: "Obtenez le tarif de votre course instantanément. Transparence totale, sans frais cachés.",
    label_pickup: "Lieu de prise en charge", label_dropoff: "Lieu de dépose", label_service: "Catégorie de prestation",
    opt_standard: "Transfert Classique", opt_hourly: "Mise à disposition (à l'heure)", opt_wedding: "Mariage (Classe E)", 
    opt_event: "Événementiel / Soirée", opt_airport: "Transfert Aéroport", opt_tour: "Circuit Touristique", opt_station: "Transfert Gare",
    btn_calc: "Voir mon tarif", btn_calculating: "Calcul en cours...",
    badge_pay: "Paiement à bord", badge_cancel: "Annulation gratuite", badge_price: "Prix fixé",
    res_fare_for: "Tarif estimé pour", res_dist: "Distance", res_time: "Temps estimé", btn_book: "Réserver ce trajet",
    res_not_found: "Itinéraire introuvable", res_error_desc: "Renseignez vos adresses pour visualiser le trajet sur la carte et découvrir votre tarif.",
    serv_tag: "Nos prestations", serv_title1: "Des services pour ", serv_title2: "chaque occasion",
    veh_tag: "Notre véhicule", veh_title1: "Le confort ", veh_title2: "à son apogée", 
    veh_desc: "Notre Mercedes Classe E Hybride est équipée des dernières technologies pour vous garantir un voyage d'exception dans un silence absolu.",
    ent_tag: "Divertissement à bord", ent_title1: "Écran passager ", ent_title2: "multimédia HD",
    ent_desc: "Profitez de votre trajet avec notre écran tactile HD intégré. Regardez vos contenus préférés pendant le voyage.",
    cont_tag: "Contact", cont_title1: "Réservez votre ", cont_title2: "chauffeur", 
    cont_desc: "Pour toute demande de réservation ou d'information, n'hésitez pas à nous contacter.",
    form_title: "Demande de réservation", form_name: "Nom complet *", form_phone: "Téléphone *", form_email: "Adresse E-mail", 
    form_pickup: "Lieu de départ", form_dropoff: "Lieu d'arrivée", form_date: "Date et Heure", form_details: "Détails de la course", 
    btn_confirm: "Confirmer la demande", btn_sending: "Envoi en cours...",
    ph_name: "Ex: Jean Dupont", ph_details: "Nombre de bagages, numéro de vol, demande particulière...",
    toast_success: "Estimation calculée avec succès !", toast_err_addr: "Veuillez sélectionner des adresses exactes dans la liste.",
    toast_sent: "Demande envoyée ! Nous vous recontactons très vite.",
    footer_desc: "Service VTC Premium en Classe E Hybride • Normandie",
    rights: "Tous droits réservés."
  },
  EN: {
    nav_sim: "Simulator", nav_serv: "Services", nav_veh: "Vehicle", nav_cont: "Contact",
    hero_tag: "Premium Chauffeur in Normandy", hero_title1: "Your Private Chauffeur", hero_title2: "in Normandy", 
    hero_desc: "Travel in First Class aboard our Mercedes E-Class Hybrid. Absolute comfort, discretion, and environmental respect for all your transfers.",
    btn_estimate: "Estimate my journey", btn_call: "Call now",
    sim_tag: "Online Simulator", sim_title1: "Estimate your ", sim_title2: "journey", 
    sim_desc: "Get your fare instantly. Total transparency, no hidden fees.",
    label_pickup: "Pick-up location", label_dropoff: "Drop-off location", label_service: "Service category",
    opt_standard: "Standard Transfer", opt_hourly: "Hourly Chauffeur", opt_wedding: "Wedding (E-Class)", 
    opt_event: "Event / Evening", opt_airport: "Airport Transfer", opt_tour: "Sightseeing Tour", opt_station: "Train Station Transfer",
    btn_calc: "Get my fare", btn_calculating: "Calculating...",
    badge_pay: "Pay on board", badge_cancel: "Free cancellation", badge_price: "Fixed price",
    res_fare_for: "Estimated fare for", res_dist: "Distance", res_time: "Estimated time", btn_book: "Book this journey",
    res_not_found: "Route not found", res_error_desc: "Fill in the fields on the left to discover our fare and see the route on the map.",
    serv_tag: "Our services", serv_title1: "Services for ", serv_title2: "every occasion",
    veh_tag: "Our vehicle", veh_title1: "Comfort ", veh_title2: "at its peak", 
    veh_desc: "Our Mercedes E-Class Hybrid is equipped with the latest technologies to guarantee you an exceptional journey in absolute silence.",
    ent_tag: "In-car entertainment", ent_title1: "Passenger ", ent_title2: "HD display",
    ent_desc: "Enjoy your ride with our integrated HD touch screen. Watch your favorite content during the trip.",
    cont_tag: "Contact", cont_title1: "Book your ", cont_title2: "chauffeur", 
    cont_desc: "For any booking request or information, please contact us.",
    form_title: "Booking Request", form_name: "Full name *", form_phone: "Phone *", form_email: "Email address", 
    form_pickup: "Pick-up location", form_dropoff: "Drop-off location", form_date: "Date and Time", form_details: "Journey details", 
    btn_confirm: "Confirm request", btn_sending: "Sending...",
    ph_name: "Ex: John Doe", ph_details: "Number of bags, flight number, special requests...",
    toast_success: "Estimate calculated successfully!", toast_err_addr: "Please select exact addresses from the dropdown list.",
    toast_sent: "Request sent! We will contact you shortly.",
    footer_desc: "Premium E-Class Hybrid VTC Service • Normandy",
    rights: "All rights reserved."
  }
};

const MapBounds = ({ routeCoords }) => {
  const map = useMap();
  useEffect(() => {
    if (routeCoords && routeCoords.length > 0) {
      map.fitBounds(routeCoords, { padding: [40, 40] });
    }
  }, [map, routeCoords]);
  return null;
};

const AddressAutocomplete = ({ value, onChange, placeholder, testId }) => {
  return (
    <div className="relative w-full">
      <div className="relative flex items-center bg-[#121212] border border-white/10 rounded-xl transition-all duration-300 focus-within:border-[#C5A059] focus-within:ring-1 focus-within:ring-[#C5A059] overflow-hidden">
        <div className="pl-4">
          <MapPinned className="text-[#C5A059]" size={20} />
        </div>
        <Autocomplete
          apiKey="AIzaSyBElgeon_TDIHU3OhqMNvkOFTyEshc_LhM"
          options={{ types: ["establishment", "geocode"], componentRestrictions: { country: "fr" } }}
          onPlaceSelected={(place) => {
            if (!place.geometry) return;
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();
            let primaryName = place.name || "";
            let secondaryName = place.formatted_address || "";
            if (secondaryName.includes(primaryName)) {
              secondaryName = secondaryName.replace(primaryName, "").replace(/^,\s*/, "");
            }
            secondaryName = secondaryName.replace(", France", "").trim();
            const fullLabel = primaryName ? `${primaryName}, ${secondaryName}` : secondaryName;
            onChange({ 
              label: fullLabel, primary: primaryName || secondaryName, 
              secondary: primaryName ? secondaryName : "", coords: [lng, lat] 
            });
          }}
          defaultValue={value.label || ""}
          className="w-full bg-transparent text-[#F5F5F0] px-4 py-4 outline-none placeholder-[#A1A1AA]"
          placeholder={placeholder}
          data-testid={testId}
        />
      </div>
    </div>
  );
};

function App() {
  const [lang, setLang] = useState("FR");
  const t = (key) => translations[lang][key];

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [priceEstimate, setPriceEstimate] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [simulatorForm, setSimulatorForm] = useState({
    departure: { label: "", primary: "", secondary: "", coords: null },
    destination: { label: "", primary: "", secondary: "", coords: null },
    service_type: "standard"
  });
  const [contactForm, setContactForm] = useState({
    name: "", email: "", phone: "", service_type: "standard",
    message: "", departure: "", destination: "", date: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const heroRef = useRef(null);
  const simulatorRef = useRef(null);
  const servicesRef = useRef(null);
  const vehicleRef = useRef(null);
  const contactRef = useRef(null);

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
    setMobileMenuOpen(false);
  };

  const handleReserveSimulation = () => {
    setContactForm({
      ...contactForm,
      departure: simulatorForm.departure.label,
      destination: simulatorForm.destination.label,
      service_type: simulatorForm.service_type
    });
    scrollToSection(contactRef);
    toast.success(lang === 'FR' ? "Détails du trajet ajoutés ! Complétez vos coordonnées pour valider." : "Journey details added! Complete your details to confirm.");
  };

  const handlePriceEstimate = async (e) => {
    e.preventDefault();
    if (!simulatorForm.departure.coords || !simulatorForm.destination.coords) {
      toast.error(t('toast_err_addr'));
      return;
    }
    setIsCalculating(true);
    try {
      const depCoords = simulatorForm.departure.coords; 
      const destCoords = simulatorForm.destination.coords; 
      const routeResponse = await fetch(`https://router.project-osrm.org/route/v1/driving/${depCoords[0]},${depCoords[1]};${destCoords[0]},${destCoords[1]}?overview=full&geometries=geojson`);
      const routeData = await routeResponse.json();

      if (routeData.code !== 'Ok' || !routeData.routes || routeData.routes.length === 0) throw new Error(t('res_not_found'));

      const distanceKm = Math.round((routeData.routes[0].distance / 1000) * 10) / 10;
      const durationMin = Math.round(routeData.routes[0].duration / 60);
      const routeCoords = routeData.routes[0].geometry.coordinates.map(coord => [coord[1], coord[0]]);

      let estimatedPrice = 0;
      if (simulatorForm.service_type === "disposition") {
        estimatedPrice = Math.max(60, Math.round((durationMin / 60) * 60));
      } else {
        const SEUIL = 50; 
        if (distanceKm <= 6) estimatedPrice = 20;
        else if (distanceKm <= SEUIL) estimatedPrice = Math.round(distanceKm * 3);
        else estimatedPrice = Math.round((SEUIL * 3) + ((distanceKm - SEUIL) * 2));

        if (simulatorForm.service_type === "mariage" || simulatorForm.service_type === "evenement") {
          estimatedPrice = Math.round(estimatedPrice * 1.3);
        }
      }
      
      const hours = Math.floor(durationMin / 60);
      const minutes = durationMin % 60;
      const durationText = lang === 'FR' ? (hours > 0 ? `${hours}h ${minutes}min` : `${minutes}min`) : (hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`);

      setPriceEstimate({
        success: true, depPrimary: simulatorForm.departure.primary, depSecondary: simulatorForm.departure.secondary,
        destPrimary: simulatorForm.destination.primary, destSecondary: simulatorForm.destination.secondary,
        estimated_price: estimatedPrice, estimated_distance_km: distanceKm, duration_estimate: durationText, routeCoords: routeCoords
      });
      toast.success(t('toast_success'));
    } catch (error) {
      toast.error(error.message || t('toast_err_addr'));
      setPriceEstimate({ success: false, message: t('res_not_found') });
    } finally { setIsCalculating(false); }
  };

  const handleContactSubmit = (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.phone) {
      toast.error(lang === 'FR' ? "Veuillez remplir au moins votre nom et téléphone" : "Please fill in at least your name and phone");
      return;
    }
    setIsSubmitting(true);
    emailjs.send('service_l8riwft', 'template_i3oxntj', { ...contactForm }, 'q9snfrsOoNj3clIIu')
    .then(() => {
      toast.success(t('toast_sent'));
      setContactForm({ name: "", email: "", phone: "", service_type: "standard", message: "", departure: "", destination: "", date: "" });
    })
    .finally(() => setIsSubmitting(false));
  };

  const getServicesList = () => [
    { icon: Heart, title: t('opt_wedding'), description: lang === 'FR' ? "Rendez votre jour J inoubliable avec notre service d'exception en Classe E." : "Make your special day unforgettable with our exceptional E-Class service.", image: SERVICE_IMAGES.mariage },
    { icon: Plane, title: t('opt_airport'), description: lang === 'FR' ? "Transferts vers CDG, Orly, Beauvais et Deauville." : "Transfers to CDG, Orly, Beauvais and Deauville.", image: SERVICE_IMAGES.aeroport },
    { icon: Train, title: t('opt_station'), description: lang === 'FR' ? "Connexions vers toutes les gares de Normandie et Paris." : "Connections to all Normandy and Paris train stations.", image: SERVICE_IMAGES.gare },
    { icon: Users, title: t('opt_event'), description: lang === 'FR' ? "Soirées, galas, séminaires professionnels." : "Evenings, galas, professional seminars.", image: SERVICE_IMAGES.evenement },
    { icon: MapPin, title: t('opt_tour'), description: lang === 'FR' ? "Découvrez les merveilles de Normandie : Mont Saint-Michel, Deauville..." : "Discover Normandy's wonders: Mont Saint-Michel, Deauville...", image: SERVICE_IMAGES.tourisme },
    { icon: Car, title: t('opt_hourly'), description: lang === 'FR' ? "Chauffeur dédié à l'heure, à la demi-journée ou journée complète." : "Dedicated chauffeur by the hour, half-day or full day.", image: SERVICE_IMAGES.disposition }
  ];

  const comfortFeatures = [
    { icon: Tv, title: lang === 'FR' ? "Écran HD passager" : "Passenger HD display" },
    { icon: Wifi, title: lang === 'FR' ? "WiFi 5G gratuit" : "Free 5G WiFi" },
    { icon: Battery, title: lang === 'FR' ? "Chargeurs USB-C" : "USB-C Chargers" },
    { icon: Armchair, title: lang === 'FR' ? "Sièges cuir chauffants" : "Heated leather seats" },
    { icon: Shield, title: lang === 'FR' ? "Climatisation bi-zone" : "Dual-zone climate" },
    { icon: Sparkles, title: lang === 'FR' ? "Rafraîchissements" : "Refreshments" }
  ];

  const entertainmentFeatures = [
    { icon: Youtube, title: "YouTube", description: lang === 'FR' ? "Vidéos et musiques" : "Videos & music" },
    { icon: Gamepad2, title: lang === 'FR' ? "Jeux" : "Games", description: lang === 'FR' ? "Divertissement interactif" : "Interactive fun" },
    { icon: Play, title: lang === 'FR' ? "Films & Séries" : "Movies & Series", description: "Netflix, Prime Video" },
    { icon: Music, title: "Spotify", description: lang === 'FR' ? "Vos playlists préférées" : "Your favorite playlists" }
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      <Toaster position="top-right" toastOptions={{ style: { background: '#1A1A1A', color: '#F5F5F0', border: '1px solid rgba(197, 160, 89, 0.5)' } }} />

      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => scrollToSection(heroRef)}>
            <span className="text-2xl font-bold text-gold-gradient font-['Playfair_Display']">SH TRANSPORT</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection(simulatorRef)} className="nav-link text-sm uppercase tracking-widest">{t('nav_sim')}</button>
            <button onClick={() => scrollToSection(servicesRef)} className="nav-link text-sm uppercase tracking-widest">{t('nav_serv')}</button>
            <button onClick={() => scrollToSection(vehicleRef)} className="nav-link text-sm uppercase tracking-widest">{t('nav_veh')}</button>
            <button onClick={() => scrollToSection(contactRef)} className="nav-link text-sm uppercase tracking-widest">{t('nav_cont')}</button>
            <button onClick={() => setLang(lang === 'FR' ? 'EN' : 'FR')} className="flex items-center gap-2 text-xs font-bold bg-[#1A1A1A] border border-[#C5A059] text-[#C5A059] px-3 py-1.5 rounded-full hover:bg-[#C5A059] hover:text-black transition-colors">
              <Globe size={14} /> {lang === 'FR' ? 'EN' : 'FR'}
            </button>
          </div>
          <div className="flex items-center gap-4 md:hidden">
            {/* BOUTON LANGUE MOBILE AMÉLIORÉ */}
            <button 
              onClick={() => setLang(lang === 'FR' ? 'EN' : 'FR')} 
              className="flex items-center justify-center gap-1.5 text-[11px] font-bold text-[#C5A059] border border-[#C5A059]/50 bg-[#C5A059]/10 px-3 py-1.5 rounded-full transition-colors active:bg-[#C5A059] active:text-black"
            >
              <Globe size={14} />
              <span>{lang === 'FR' ? 'EN' : 'FR'}</span>
            </button>
            
            <button className="text-[#C5A059]" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden glass border-t border-white/10 px-6 py-6 space-y-4">
            <button onClick={() => scrollToSection(simulatorRef)} className="block w-full text-left nav-link py-2">{t('nav_sim')}</button>
            <button onClick={() => scrollToSection(servicesRef)} className="block w-full text-left nav-link py-2">{t('nav_serv')}</button>
            <button onClick={() => scrollToSection(vehicleRef)} className="block w-full text-left nav-link py-2">{t('nav_veh')}</button>
            <button onClick={() => scrollToSection(contactRef)} className="block w-full text-left nav-link py-2">{t('nav_cont')}</button>
          </div>
        )}
      </nav>

      <section ref={heroRef} className="hero-section">
        <video autoPlay muted loop playsInline className="hero-video"><source src={HERO_VIDEO} type="video/mp4" /></video>
        <div className="video-overlay absolute inset-0" />
        <div className="hero-content min-h-screen flex items-center px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl pt-32 pb-20">
            <p className="text-[#C5A059] uppercase tracking-[0.3em] text-sm mb-6">{t('hero_tag')}</p>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-[#F5F5F0] leading-tight mb-8">{t('hero_title1')}<br /><span className="text-gold-gradient">{t('hero_title2')}</span></h1>
            <p className="text-lg text-[#A1A1AA] mb-10 max-w-xl">{t('hero_desc')}</p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button onClick={() => scrollToSection(simulatorRef)} className="btn-gold bg-[#C5A059] text-black font-semibold uppercase tracking-widest px-8 py-4 hover:bg-[#E5C580] transition-colors rounded-lg">{t('btn_estimate')}</button>
              <a href={PHONE_LINK} className="flex items-center justify-center gap-2 bg-black/50 backdrop-blur-md border border-[#C5A059]/50 text-[#C5A059] font-semibold uppercase tracking-widest px-8 py-4 rounded-lg hover:bg-[#C5A059]/10 transition-colors"><Phone size={20} /><span>{t('btn_call')}</span></a>
            </div>
          </div>
        </div>
      </section>

      <section ref={simulatorRef} className="py-24 md:py-32 bg-[#050505] relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#C5A059]/5 blur-[150px] rounded-full pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10 grid lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-7">
            <p className="text-[#C5A059] uppercase tracking-[0.3em] text-sm mb-4 font-semibold">{t('sim_tag')}</p>
            <h2 className="text-4xl md:text-5xl font-['Playfair_Display'] text-[#F5F5F0] mb-6">{t('sim_title1')}<span className="text-gold-gradient italic">{t('sim_title2')}</span></h2>
            <p className="text-[#A1A1AA] mb-10 text-lg">{t('sim_desc')}</p>
            <form onSubmit={handlePriceEstimate} className="space-y-6">
              <div><label className="block text-xs font-semibold text-[#888888] uppercase tracking-widest mb-3 pl-2">{t('label_pickup')}</label><AddressAutocomplete value={simulatorForm.departure} onChange={(val) => setSimulatorForm({...simulatorForm, departure: val})} placeholder="Ex: Gare de Caen..." /></div>
              <div><label className="block text-xs font-semibold text-[#888888] uppercase tracking-widest mb-3 pl-2">{t('label_dropoff')}</label><AddressAutocomplete value={simulatorForm.destination} onChange={(val) => setSimulatorForm({...simulatorForm, destination: val})} placeholder="Ex: Paris..." /></div>
              <div><label className="block text-xs font-semibold text-[#888888] uppercase tracking-widest mb-3 pl-2">{t('label_service')}</label>
                <div className="relative bg-[#121212] border border-white/10 rounded-xl transition-all duration-300 focus-within:border-[#C5A059]">
                  <select value={simulatorForm.service_type} onChange={(e) => setSimulatorForm({...simulatorForm, service_type: e.target.value})} className="w-full bg-transparent text-[#F5F5F0] px-4 py-4 outline-none cursor-pointer appearance-none">
                    <option value="standard" className="bg-[#1A1A1A]">{t('opt_standard')}</option>
                    <option value="disposition" className="bg-[#1A1A1A]">{t('opt_hourly')}</option>
                    <option value="mariage" className="bg-[#1A1A1A]">{t('opt_wedding')}</option>
                    <option value="evenement" className="bg-[#1A1A1A]">{t('opt_event')}</option>
                  </select>
                </div>
              </div>
              <button type="submit" disabled={isCalculating} className="w-full bg-gradient-to-r from-[#C5A059] to-[#E5C580] text-black font-bold uppercase tracking-widest px-8 py-5 rounded-xl hover:shadow-[0_0_20px_rgba(197,160,89,0.4)] transition-all disabled:opacity-70 mt-4 text-sm">{isCalculating ? t('btn_calculating') : t('btn_calc')}</button>
            </form>
            <div className="grid grid-cols-3 gap-4 mt-10 pt-8 border-t border-white/10">
              <div className="flex flex-col items-center text-center gap-2"><CreditCard className="text-[#C5A059]" size={24} /><span className="text-xs text-[#888888] uppercase tracking-wider">{t('badge_pay')}</span></div>
              <div className="flex flex-col items-center text-center gap-2"><RefreshCcw className="text-[#C5A059]" size={24} /><span className="text-xs text-[#888888] uppercase tracking-wider">{t('badge_cancel')}</span></div>
              <div className="flex flex-col items-center text-center gap-2"><CheckCircle className="text-[#C5A059]" size={24} /><span className="text-xs text-[#888888] uppercase tracking-wider">{t('badge_price')}</span></div>
            </div>
          </div>

          <div className="lg:col-span-5 h-full">
            <div className="bg-[#121212] border border-white/10 rounded-2xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.5)] flex flex-col relative h-full min-h-[500px]">
              {priceEstimate && priceEstimate.success ? (
                <div className="flex flex-col h-full animate-fade-in-up w-full">
                  <div className="h-56 w-full bg-[#1A1A1A] relative border-b border-white/5">
                    <MapContainer center={priceEstimate.routeCoords[0]} zoom={10} style={{ height: '100%', width: '100%', zIndex: 10 }} zoomControl={false} dragging={false} scrollWheelZoom={false}>
                      <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
                      <Polyline positions={priceEstimate.routeCoords} color="#C5A059" weight={4} opacity={0.8} />
                      <CircleMarker center={priceEstimate.routeCoords[0]} radius={5} color="#C5A059" fillColor="#121212" fillOpacity={1} weight={3} />
                      <CircleMarker center={priceEstimate.routeCoords[priceEstimate.routeCoords.length - 1]} radius={5} color="#F5F5F0" fillColor="#121212" fillOpacity={1} weight={3} />
                      <MapBounds routeCoords={priceEstimate.routeCoords} />
                    </MapContainer>
                    <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#121212] to-transparent z-[400] pointer-events-none" />
                  </div>
                  <div className="p-8 flex-1 flex flex-col justify-between">
                    <div className="space-y-6 mb-8 relative">
                       <div className="absolute left-[11px] top-4 bottom-8 w-[2px] bg-gradient-to-b from-[#C5A059] to-white/20" />
                       <div className="relative pl-10">
                          <div className="absolute left-0 top-1 w-6 h-6 rounded-full border-[2px] border-[#C5A059] bg-[#121212] flex items-center justify-center shadow-[0_0_10px_rgba(197,160,89,0.3)] z-10"><div className="w-1.5 h-1.5 bg-[#C5A059] rounded-full" /></div>
                          <p className="text-[#F5F5F0] font-semibold text-lg leading-tight">{priceEstimate.depPrimary}</p>
                          {priceEstimate.depSecondary && <p className="text-[#888888] text-xs mt-1 line-clamp-1">{priceEstimate.depSecondary}</p>}
                       </div>
                       <div className="relative pl-10">
                          <div className="absolute left-0 top-1 w-6 h-6 rounded-full border-[2px] border-white/60 bg-[#121212] flex items-center justify-center z-10"><div className="w-1.5 h-1.5 bg-white/60 rounded-full" /></div>
                          <p className="text-[#F5F5F0] font-semibold text-lg leading-tight">{priceEstimate.destPrimary}</p>
                          {priceEstimate.destSecondary && <p className="text-[#888888] text-xs mt-1 line-clamp-1">{priceEstimate.destSecondary}</p>}
                       </div>
                    </div>
                    <div className="pt-6 border-t border-white/5 text-center">
                      <p className="text-[#888888] text-[10px] uppercase tracking-widest mb-2">{t('res_fare_for')}</p>
                      <div className="text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#C5A059] to-[#E5C580] mb-6 font-['Playfair_Display']">{priceEstimate.estimated_price}€</div>
                      <div className="grid grid-cols-2 gap-4 text-center mb-6 bg-[#1A1A1A] rounded-xl p-4 border border-white/5">
                        <div><p className="text-[#888888] text-[10px] uppercase tracking-wider mb-1">{t('res_dist')}</p><p className="text-[#F5F5F0] text-sm font-bold font-mono">~{priceEstimate.estimated_distance_km} km</p></div>
                        <div className="border-l border-white/10"><p className="text-[#888888] text-[10px] uppercase tracking-wider mb-1">{t('res_time')}</p><p className="text-[#F5F5F0] text-sm font-bold font-mono">{priceEstimate.duration_estimate}</p></div>
                      </div>
                      <button onClick={handleReserveSimulation} className="w-full bg-gradient-to-r from-[#C5A059] to-[#E5C580] text-black font-bold uppercase tracking-widest px-6 py-4 rounded-xl hover:shadow-[0_0_20px_rgba(197,160,89,0.4)] transition-all text-xs">{t('btn_book')}</button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-full p-10 text-center relative z-10">
                  <div className="w-20 h-20 mb-8 border-2 border-[#C5A059]/20 bg-[#C5A059]/5 rounded-full flex items-center justify-center"><MapPinned className="text-[#C5A059]" size={32} /></div>
                  <h3 className="text-2xl text-[#F5F5F0] mb-3 font-['Playfair_Display']">{t('nav_sim')}</h3>
                  <p className="text-[#888888] text-sm max-w-[250px] leading-relaxed">{t('res_error_desc')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section ref={servicesRef} className="py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="text-[#C5A059] uppercase tracking-[0.3em] text-sm mb-4">{t('serv_tag')}</p>
            <h2 className="section-title text-[#F5F5F0]">{t('serv_title1')}<br /><span className="text-gold-gradient">{t('serv_title2')}</span></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {getServicesList().map((s, i) => (
              <div key={i} className="service-card bg-[#121212] border border-white/5 overflow-hidden group rounded-xl">
                <div className="relative h-56 overflow-hidden"><img src={s.image} alt={s.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" /><div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-[#121212]/20 to-transparent" /><div className="absolute top-4 left-4 w-10 h-10 bg-[#C5A059] rounded-lg flex items-center justify-center shadow-lg"><s.icon className="text-black" size={20} /></div></div>
                <div className="p-6"><h3 className="text-xl text-[#F5F5F0] mb-3">{s.title}</h3><p className="text-[#A1A1AA] text-sm leading-relaxed">{s.description}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section ref={vehicleRef} className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 grid lg:grid-cols-2 gap-16 items-center">
          <div className="vehicle-image relative overflow-hidden rounded-xl">
            <img src={CAR_IMAGE} alt="Mercedes Class E" className="w-full h-auto" />
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent"><p className="text-[#C5A059] font-mono text-sm">Mercedes-Benz Classe E Hybride</p><p className="text-[#F5F5F0] text-xl">Berline Premium Éco-responsable</p></div>
          </div>
          <div>
            <p className="text-[#C5A059] uppercase tracking-[0.3em] text-sm mb-4">{t('veh_tag')}</p>
            <h2 className="section-title text-[#F5F5F0]">{t('veh_title1')}<br /><span className="text-gold-gradient">{t('veh_title2')}</span></h2>
            <p className="section-subtitle mt-4 mb-10">{t('veh_desc')}</p>
            <div className="grid grid-cols-2 gap-6">
              {comfortFeatures.map((f, i) => (
                <div key={i} className="feature-card group"><div className="feature-icon mb-4"><f.icon size={24} /></div><h4 className="text-[#F5F5F0] font-medium mb-1">{f.title}</h4></div>
              ))}
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 mt-24 grid lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1">
            <p className="text-[#C5A059] uppercase tracking-[0.3em] text-sm mb-4">{t('ent_tag')}</p>
            <h2 className="section-title text-[#F5F5F0]">{t('ent_title1')}<br /><span className="text-gold-gradient">{t('ent_title2')}</span></h2>
            <p className="section-subtitle mt-4 mb-10">{t('ent_desc')}</p>
            <div className="grid grid-cols-2 gap-4">
              {entertainmentFeatures.map((f, i) => (
                <div key={i} className="flex items-center gap-4 p-4 bg-[#121212] border border-white/5 rounded-xl transition-colors"><div className="w-10 h-10 bg-[#C5A059]/10 rounded-lg flex items-center justify-center"><f.icon className="text-[#C5A059]" size={20} /></div><div><p className="text-[#F5F5F0] font-medium text-sm">{f.title}</p><p className="text-[#52525B] text-xs">{f.description}</p></div></div>
              ))}
            </div>
          </div>
          <div className="order-1 lg:order-2 vehicle-image relative overflow-hidden rounded-xl"><img src={CAR_INTERIOR_IMAGE} alt="Interior display" className="w-full h-auto" /><div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent"><p className="text-[#C5A059] font-mono text-sm">HD display</p><p className="text-[#F5F5F0] text-xl">YouTube, Games, Movies</p></div></div>
        </div>
      </section>

      <section ref={contactRef} className="py-24 md:py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 grid lg:grid-cols-2 gap-16">
          <div className="space-y-6">
            <p className="text-[#C5A059] uppercase tracking-[0.3em] text-sm mb-4">{t('cont_tag')}</p>
            <h2 className="section-title text-[#F5F5F0]">{t('cont_title1')}<br /><span className="text-gold-gradient">{t('cont_title2')}</span></h2>
            <div className="space-y-6 pt-10">
              <a href={PHONE_LINK} className="flex items-center gap-4 group"><div className="w-14 h-14 border border-[#C5A059]/30 rounded-xl flex items-center justify-center group-hover:bg-[#C5A059] transition-all"><Phone className="text-[#C5A059] group-hover:text-black" size={24} /></div><div><p className="text-sm text-[#A1A1AA] uppercase">Téléphone</p><p className="text-xl sm:text-2xl text-[#F5F5F0] font-mono">{PHONE_NUMBER}</p></div></a>
              <a href={EMAIL_LINK} className="flex items-center gap-4 group"><div className="w-14 h-14 border border-[#C5A059]/30 rounded-xl flex items-center justify-center group-hover:bg-[#C5A059] transition-all"><Mail className="text-[#C5A059] group-hover:text-black" size={24} /></div><div><p className="text-sm text-[#A1A1AA] uppercase">Email</p><p className="text-xl text-[#F5F5F0]">{EMAIL_ADDRESS}</p></div></a>
              <a href={FACEBOOK_LINK} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group"><div className="w-14 h-14 border border-[#C5A059]/30 rounded-xl flex items-center justify-center group-hover:bg-[#C5A059] transition-all"><Facebook className="text-[#C5A059] group-hover:text-black" size={24} /></div><div><p className="text-sm text-[#A1A1AA] uppercase">Facebook</p><p className="text-xl text-[#F5F5F0]">SH Transport</p></div></a>
              <div className="flex items-center gap-4"><div className="w-14 h-14 border border-[#C5A059]/30 rounded-xl flex items-center justify-center"><MapPin className="text-[#C5A059]" size={24} /></div><div><p className="text-sm text-[#A1A1AA] uppercase">Zone</p><p className="text-xl text-[#F5F5F0]">Caen & Normandie</p></div></div>
              <div className="flex items-center gap-4"><div className="w-14 h-14 border border-[#C5A059]/30 rounded-xl flex items-center justify-center"><Clock className="text-[#C5A059]" size={24} /></div><div><p className="text-sm text-[#A1A1AA] uppercase">{lang === 'FR' ? "Disponibilité" : "Availability"}</p><p className="text-xl text-[#F5F5F0]">7j/7 - 24h/24</p></div></div>
            </div>
          </div>
          <div className="simulator-container p-8 md:p-10 rounded-2xl border border-white/5 bg-[#121212]">
            <h3 className="text-2xl text-[#F5F5F0] mb-8 font-['Playfair_Display']">{t('form_title')}</h3>
            <form onSubmit={handleContactSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div><label className="block text-xs font-semibold text-[#888888] mb-2">{t('form_name')}</label><input type="text" value={contactForm.name} onChange={(e) => setContactForm({...contactForm, name: e.target.value})} className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-4 py-4 text-[#F5F5F0] outline-none focus:border-[#C5A059]" placeholder={t('ph_name')} required /></div>
                <div><label className="block text-xs font-semibold text-[#888888] mb-2">{t('form_phone')}</label><input type="tel" value={contactForm.phone} onChange={(e) => setContactForm({...contactForm, phone: e.target.value})} className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-4 py-4 text-[#F5F5F0] outline-none focus:border-[#C5A059]" placeholder="06..." required /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div><label className="block text-xs font-semibold text-[#888888] mb-2">{t('form_pickup')}</label><input type="text" value={contactForm.departure} onChange={(e) => setContactForm({...contactForm, departure: e.target.value})} className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-4 py-4 text-[#F5F5F0] focus:border-[#C5A059] outline-none" /></div>
                <div><label className="block text-xs font-semibold text-[#888888] mb-2">{t('form_dropoff')}</label><input type="text" value={contactForm.destination} onChange={(e) => setContactForm({...contactForm, destination: e.target.value})} className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-4 py-4 text-[#F5F5F0] focus:border-[#C5A059] outline-none" /></div>
              </div>
              <div><label className="block text-xs font-semibold text-[#888888] mb-2">{t('form_date')}</label><input type="datetime-local" value={contactForm.date} onChange={(e) => setContactForm({...contactForm, date: e.target.value})} className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-4 py-4 text-[#F5F5F0] focus:border-[#C5A059] outline-none color-scheme-dark" /></div>
              <div><label className="block text-xs font-semibold text-[#888888] mb-2">{t('form_details')}</label><textarea value={contactForm.message} onChange={(e) => setContactForm({...contactForm, message: e.target.value})} rows={4} className="w-full bg-[#1A1A1A] border border-white/5 rounded-xl px-4 py-4 text-[#F5F5F0] outline-none resize-none" placeholder={t('ph_details')} /></div>
              <button type="submit" disabled={isSubmitting} className="bg-gradient-to-r from-[#C5A059] to-[#E5C580] text-black font-bold uppercase tracking-widest px-8 py-5 rounded-xl w-full flex justify-center gap-3 mt-6 text-sm hover:shadow-[0_0_20px_rgba(197,160,89,0.4)] transition-all"><Send size={20} /><span>{isSubmitting ? t('btn_sending') : t('btn_confirm')}</span></button>
            </form>
          </div>
        </div>
      </section>

      {/* --- FOOTER RESTAURÉ --- */}
      <footer className="py-12 bg-[#050505] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-center md:text-left">
              <p className="text-2xl font-bold text-gold-gradient font-['Playfair_Display'] mb-2">SH TRANSPORT</p>
              <p className="text-[#A1A1AA] text-sm">{t('footer_desc')}</p>
            </div>
            <div className="flex items-center gap-6">
              <a href={PHONE_LINK} className="text-[#C5A059] hover:text-[#E5C580] transition-colors flex items-center gap-2 whitespace-nowrap">
                <Phone size={18} /><span className="font-mono">{PHONE_NUMBER}</span>
              </a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-white/5 text-center">
            <p className="text-[#52525B] text-sm">© 2026 SH TRANSPORT. {t('rights')}</p>
          </div>
        </div>
      </footer>

      {/* --- BOUTON WHATSAPP RESTAURÉ --- */}
      <a 
        href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"
        style={{
          position: 'fixed', bottom: '24px', right: '24px', backgroundColor: '#25D366', color: 'white',
          padding: '16px', borderRadius: '50px', boxShadow: '0 8px 24px rgba(37, 211, 102, 0.4)',
          zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          textDecoration: 'none', fontWeight: 'bold', transition: 'transform 0.2s ease'
        }}
        onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
        onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
      >
        <span style={{ marginRight: '8px', fontSize: '24px' }}>💬</span> WhatsApp
      </a>
    </div>
  );
}

export default App;